import { RestomenuError } from "@restomenu/core/client";
import type { ApiError } from "@restomenu/core/client";
import { showGlobalSnackbar } from "@/context/snackbarBridge";
import {
  ORDER_NOT_FOUND_CODE,
  ORDER_PATHS,
  OPTION_NOT_FOUND_CODE,
  OPTION_UNAVAILABLE_CODE,
  OPTION_BLOCK_LIMIT_CODE,
  OPTION_QUANTITY_LIMIT_CODE,
  ITEMS_MUTATION_CONFLICT_CODE,
  ITEMS_QUANTITY_LIMIT_CODE,
  blockIdFromErrorPath,
} from "@/core/orders/constants";

/**
 * Справочник кодов заказа, приведённый к схеме REST (§4.3 спеки Ф7).
 *
 * Прежние строки достались от GraphQL и разъехались с контрактом: 1329 —
 * это «заказ не найден», а вовсе не «продукт не привязан к точке». Коды
 * 1322, 1325 и 13210 удалены: в REST их нет, и оставленная строка подписала
 * бы чужой код.
 *
 * Гостю адресованы не все — 1326, 13211-13212, 13215-13219 суть ветки
 * сотрудника либо из виджета недостижимы. Держим всё равно: «Неизвестная
 * ошибка» на известный код хуже строки, которую пользователь не должен был
 * увидеть.
 *
 * Выключенная точка и точка чужого бренда отвечают гостю тем же, чем
 * несуществующая, — отсюда формулировки 1321 и 1324 про недоступность, а не
 * про зону доставки.
 *
 * Обновление контракта 2026-09-08 сняло 1323 (OrderCustomerError) из кодов
 * POST /orders. Строка удалена, а не оставлена «на всякий случай»: код
 * освободился, и подпись про гостя досталась бы его следующему владельцу —
 * ровно то, от чего предостерегает абзац выше.
 */
const ORDER_ERROR_DICTIONARY: Record<number, string> = {
  1321: "Точка недоступна",
  1324: "Меню точки недоступно",
  1326: "Точка временно не принимает заказы",
  1327: "Продукт в заказе не существует",
  1328: "Товар больше не продаётся",
  [ORDER_NOT_FOUND_CODE]: "Заказ не найден",
  13211: "Ошибка сотрудника",
  13212: "Сотрудник не привязан к точке или её бренду",
  13213: "Позиция заказа не найдена",
  13214: "Позиция уже удалена",
  13215: "Точка другого бренда",
  13216: "Статус заказа менять нельзя",
  13217: "Заказ уже оформлен",
  13218: "Заказ оформляется подтверждением брони",
  13219: "Заказ пустой",
  // Доменные коды выбора опций (спека Ф8, задача 9). Клиентская проверка
  // min/max в карточке товара делает их недостижимыми в обычной работе —
  // сюда попадает только гонка с правкой меню из админки, пока карточка
  // открыта или уже закрылась.
  //
  // 13220 у схемы покрывает три разные причины (блока нет в меню, его
  // группа не привязана к товару, опция не привязана к блоку) — гостю они
  // неразличимы и не нужны порознь, одна строка на все три сознательно.
  [OPTION_NOT_FOUND_CODE]: "Опция больше не существует",
  [OPTION_UNAVAILABLE_CODE]: "Опция больше не продаётся",
  // Показывается для тех ошибок 13222, чей blockId обработчик НЕ подтвердил
  // как отрисованный (карточка уже закрыта либо открыта для другого товара,
  // см. докблок setOptionBlockErrorHandler) — сам блок на экране в этот
  // момент не подписать, отсюда общая формулировка вместо адресной
  // «Проверьте выбор в этом блоке».
  [OPTION_BLOCK_LIMIT_CODE]: "Не соблюдены условия выбора опций",
  [OPTION_QUANTITY_LIMIT_CODE]: "Указано недопустимое количество опции",
  [ITEMS_MUTATION_CONFLICT_CODE]: "Состав заказа правится двумя способами сразу",
  [ITEMS_QUANTITY_LIMIT_CODE]: "В заказе слишком много позиций",
};

/**
 * С круга 9 схемы (2026-09-03) `key`, `message` и `code` у ApiError обязательны,
 * необязателен только `path`. Гарды ниже при этом остаются: тип описывает то,
 * что обещает схема, а сюда приходит РАЗОБРАННОЕ тело ответа, которое схему
 * могло и не читать — прокси, заглушка, чужой сервис. Тот же довод, по которому
 * в core живёт isApiErrorList.
 */
function messageOf(e: ApiError): string {
  const known =
    e.code !== undefined ? ORDER_ERROR_DICTIONARY[e.code] : undefined;
  return known ?? e.message ?? e.key ?? "Ошибка запроса";
}

/**
 * Путь запроса.
 *
 * Обычный ключ openapi-react-query — `[method, path, init?]`, путь во
 * втором элементе. Но обход страниц написан руками и живёт под своим
 * ключом (`["guest-products", "list", pointId]`), где второй элемент — не
 * путь, а слово `"list"`. Такой запрос не опознался бы гостевым, и его 400
 * или 404 уехал бы гостю снекбаром — ровно то, что подавление ниже и
 * запрещает. Поэтому ручные запросы объявляют путь в `meta`, и он главнее.
 */
function pathOf(
  queryKey: readonly unknown[] | undefined,
  meta?: Record<string, unknown>,
): string {
  if (typeof meta?.path === "string") return meta.path;
  return typeof queryKey?.[1] === "string" ? queryKey[1] : "";
}

/**
 * Мост «карточка товара открыта» для 13222 (задача 9), по образцу
 * snackbarBridge.ts.
 *
 * Обработчик получает ВСЕ id нарушенных блоков из ответа и обязан вернуть
 * подмножество, которое реально отрисовал у себя, — именно оно, а не факт
 * регистрации самого обработчика, решает, для каких ошибок гасить снекбар.
 *
 * Так и должно быть: «обработчик зарегистрирован» означает лишь «какая-то
 * карточка товара сейчас открыта», а НЕ «открыта именно та карточка, которая
 * породила эту ошибку». `handleFormSubmit` в ProductPage закрывает карточку
 * СИНХРОННО, а дебаунс синхронизации (useCartSync) летит 300мс — за это
 * время гость успевает открыть карточку ДРУГОГО товара (например, через
 * RecommendationsBlock), и её обработчик регистрируется раньше ответа
 * сервера на действие из прежней карточки (найдено ревью задачи 9). blockId
 * — uuid, так что у чужой карточки его не найти никогда; без фильтрации по
 * возвращённому подмножеству ошибка ушла бы в пустоту: не подсветилась (нет
 * такого блока) и не показалась снекбаром (обработчик подавил её как
 * «показанную»).
 */
type OptionBlockErrorHandler = (blockIds: string[]) => string[];
let optionBlockErrorHandler: OptionBlockErrorHandler | null = null;

export function setOptionBlockErrorHandler(
  handler: OptionBlockErrorHandler | null
): void {
  optionBlockErrorHandler = handler;
}

/**
 * Протухший идентификатор заказа — состояние штатное, а не сбой.
 *
 * Судим по доменному коду И по пути: одного мало. MutationCache зовёт
 * reportError БЕЗ ключа (api-client.ts), а мёртвый заказ чаще обнаруживает
 * PATCH, чем чтение; ответ же на чтение бывает и без тела с кодом.
 */
function isOrderGone(error: RestomenuError, path: string): boolean {
  if (error.status !== 404) return false;
  return (
    error.errors.some((e) => e.code === ORDER_NOT_FOUND_CODE) ||
    ORDER_PATHS.some((p) => p === path)
  );
}

/**
 * Показ ошибки пользователю. Замена onError-линка Apollo.
 *
 * Молчим на 401 и 403 всегда:
 *
 *   401 — обрабатывает бутстрап сессии: её ещё нет, для пользователя это
 *         не ошибка;
 *   403 — ролевой гейт админских маршрутов, гостя он не касается.
 *
 * И дополнительно на 400/404 у гостевых маршрутов: там это не сбой, а
 * состояние экрана — «точки нет» (1509/1654), «товара нет» (11714) либо
 * негодный `pointId` в ссылке (`transport.invalid`). Рисует его DataLoader,
 * и снекбар поверх был бы вторым сообщением об одном и том же. Хуже: на
 * один неверный pointId таких ответов приходит шесть — запросов меню
 * параллельно шесть, и каждый отвечает своим.
 *
 * Вне `/guest/*` 400 и 404 остаются видимыми: там это дефект клиента —
 * кроме 404 заказа, который означает «заказа нет либо он чужой» и уводит в
 * восстановление черновика (§3.5 спеки Ф7).
 *
 * 13220, 13221 и 13223 (задача 9) отдельной ветки не заводят — идут тем же
 * циклом снекбаром, справочник ORDER_ERROR_DICTIONARY знает их подписи.
 * 13222 (`OrderOptionBlockLimitError`) особый: снекбар гасится ТОЛЬКО для
 * тех ошибок, чей blockId обработчик вернул как реально отрисованный (см.
 * докблок setOptionBlockErrorHandler) — не по факту, что обработчик вообще
 * зарегистрирован.
 */
export function reportError(
  error: unknown,
  queryKey?: readonly unknown[],
  meta?: Record<string, unknown>,
): void {
  if (error instanceof RestomenuError) {
    if (error.status === 401 || error.status === 403) return;

    const path = pathOf(queryKey, meta);

    const isGuestPath = path.startsWith("/guest/");
    if (isGuestPath && (error.status === 400 || error.status === 404)) {
      return;
    }

    // Протухший persistedOrderId уводит в восстановление черновика молча —
    // красный снекбар поверх был бы сообщением о том, что уже починено.
    if (isOrderGone(error, path)) return;

    // 13222: сопоставляем каждой ошибке ответа её blockId (для остальных
    // кодов — null, см. blockIdFromErrorPath) одним проходом — пригодится
    // и для вызова обработчика, и для решения по каждой ошибке ниже.
    const errorBlockIds = error.errors.map(
      (e) => [e, blockIdFromErrorPath(e)] as const,
    );
    const blockIds = errorBlockIds
      .map(([, blockId]) => blockId)
      .filter((id): id is string => id !== null);

    // Обработчик зовём, только если есть что показать — иначе «пустой»
    // вызов на каждую ошибку без blockId. Он возвращает подмножество,
    // которое реально отрисовал: не всё переданное автоматически «показано»
    // (обработчик может принадлежать карточке ДРУГОГО товара, см. докблок
    // setOptionBlockErrorHandler) — снекбар гасится строго по этому
    // подмножеству, а не по факту регистрации обработчика.
    const handler = optionBlockErrorHandler;
    const shownBlockIds = new Set(
      handler && blockIds.length > 0 ? handler(blockIds) : [],
    );

    for (const [e, blockId] of errorBlockIds) {
      if (blockId !== null && shownBlockIds.has(blockId)) continue;
      showGlobalSnackbar({ message: messageOf(e), severity: "error" });
    }
    return;
  }
  const message =
    error instanceof Error ? error.message : "Неизвестная ошибка сети";
  showGlobalSnackbar({ message, severity: "error" });
}
